import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

interface PageTrackingOptions {
  pageName?: string;
  additionalData?: Record<string, unknown>;
}

export const usePageTracking = (options: PageTrackingOptions = {}) => {
  const location = useLocation();
  
  useEffect(() => {
    const trackPageView = async () => {
      const sessionId = getOrCreateSessionId();
      const userId = await getCurrentUserId();
      
      try {
        await supabase.from('analytics_events').insert({
          event_type: 'page_view',
          session_id: sessionId,
          user_id: userId,
          page_url: location.pathname + location.search,
          referrer: document.referrer || null,
          browser: getBrowserInfo(),
          device_type: getDeviceType(),
          event_data: {
            page_name: options.pageName || document.title,
            page_title: document.title,
            query_params: Object.fromEntries(new URLSearchParams(location.search)),
            viewport: {
              width: window.innerWidth,
              height: window.innerHeight
            },
            screen: {
              width: screen.width,
              height: screen.height
            },
            language: navigator.language,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            ...options.additionalData
          }
        });
      } catch (error) {
        console.debug('Failed to track page view:', error);
      }
    };

    // Track after a small delay to ensure page is rendered
    const timeoutId = setTimeout(trackPageView, 100);
    
    return () => clearTimeout(timeoutId);
  }, [location.pathname, location.search, options.pageName]);
};

// Helper functions
const getOrCreateSessionId = (): string => {
  const key = 'analytics_session_id';
  let sessionId = sessionStorage.getItem(key);
  
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem(key, sessionId);
  }
  
  return sessionId;
};

const getCurrentUserId = async (): Promise<string | null> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.user?.id || null;
  } catch {
    return null;
  }
};

const getBrowserInfo = (): string => {
  const ua = navigator.userAgent;
  
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  
  return 'Other';
};

const getDeviceType = (): string => {
  const ua = navigator.userAgent;
  
  if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return 'mobile';
  
  return 'desktop';
};

// Custom event tracking
export const trackEvent = async (
  eventType: string,
  eventData: Record<string, unknown> = {}
) => {
  const sessionId = getOrCreateSessionId();
  const userId = await getCurrentUserId();
  
  try {
    await supabase.from('analytics_events').insert({
      event_type: eventType,
      session_id: sessionId,
      user_id: userId,
      page_url: window.location.pathname,
      browser: getBrowserInfo(),
      device_type: getDeviceType(),
      event_data: {
        ...eventData,
        timestamp: new Date().toISOString()
      }
    });
  } catch (error) {
    console.debug('Failed to track event:', error);
  }
};

// Specific event trackers
export const trackAddToCart = (productId: string, quantity: number, price: number) => {
  trackEvent('add_to_cart', { productId, quantity, price, value: quantity * price });
};

export const trackPurchase = (orderId: string, total: number, items: unknown[]) => {
  trackEvent('purchase', { orderId, total, items, itemCount: items.length });
};

export const trackSearch = (query: string, resultsCount: number) => {
  trackEvent('search', { query, resultsCount });
};

export const trackProductView = (productId: string, productName: string) => {
  trackEvent('product_view', { productId, productName });
};

export const trackSignUp = (method: string) => {
  trackEvent('sign_up', { method });
};

export const trackLogin = (method: string) => {
  trackEvent('login', { method });
};

export const trackError = (errorType: string, errorMessage: string, context?: Record<string, unknown>) => {
  trackEvent('error', { errorType, errorMessage, ...context });
};
